import React, { useMemo } from 'react';
import { Paper, Typography, Box, Stack, Skeleton, useTheme } from '@mui/material';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { useGetReportRevenue } from '../../hooks/reports/useGetReportRevenue';
import useCurrency from '../../hooks/common/useCurrency';
import { formatNumber } from '../../utils/money';
import ExportButton from '../exports/ExportButton';

const RevenueChart = ({ filter }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const theme = useTheme();
    const { format: formatPrice, locale } = useCurrency();

    const { series: rawSeries, isLoading } = useGetReportRevenue({
        from: filter.from,
        to: filter.to,
        granularity: filter.granularity,
    });

    const { chartData, totals } = useMemo(() => {
        const chartData = rawSeries.map((p) => {
            const d = dayjs(p.date);
            const label =
                filter.granularity === 'month'
                    ? d.format('MMM YYYY')
                    : d.format('MMM D');
            return {
                label,
                revenue: Number(p.revenue) || 0,
                orders: Number(p.orders_count) || 0,
            };
        });
        const totals = rawSeries.reduce(
            (acc, p) => {
                acc.revenue += Number(p.revenue) || 0;
                acc.orders += Number(p.orders_count) || 0;
                return acc;
            },
            { revenue: 0, orders: 0 }
        );
        return { chartData, totals };
    }, [rawSeries, filter.granularity]);

    const isEmpty = !isLoading && chartData.length === 0;

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                mb: 3,
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
                mb={2}
            >
                <Typography variant="h6" fontWeight={700}>
                    {isArabic ? 'الإيرادات بمرور الوقت' : 'Revenue over time'}
                </Typography>
                <Stack direction="row" spacing={3} alignItems="center">
                    <ExportButton
                        type="revenue"
                        filters={{
                            from: filter.from,
                            to: filter.to,
                            granularity: filter.granularity,
                        }}
                    />
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            {isArabic ? 'الإجمالي' : 'Total'}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={700}>
                            {formatPrice(totals.revenue)}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            {isArabic ? 'عدد الطلبات' : 'Orders'}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={700}>
                            {formatNumber(totals.orders, locale)}
                        </Typography>
                    </Box>
                </Stack>
            </Stack>

            {isLoading ? (
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
            ) : isEmpty ? (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                        {isArabic ? 'لا توجد بيانات للفترة المحددة' : 'No data for the selected range'}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ width: '100%', height: 320 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={theme.palette.success.main} stopOpacity={0.5} />
                                    <stop offset="95%" stopColor={theme.palette.success.main} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                            <XAxis dataKey="label" tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                            <YAxis
                                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                                tickFormatter={(v) => formatNumber(v, locale)}
                                width={70}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: theme.palette.background.paper,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 8,
                                }}
                                formatter={(value, name) => {
                                    if (name === 'revenue') return [formatPrice(value), isArabic ? 'الإيرادات' : 'Revenue'];
                                    return [formatNumber(value, locale), name];
                                }}
                            />
                            <Area
                                type="monotone"
                                dataKey="revenue"
                                stroke={theme.palette.success.main}
                                strokeWidth={2}
                                fill="url(#revenueFill)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </Paper>
    );
};

export default RevenueChart;
